import { ClearOutlined } from '@ant-design/icons';
import {
  Space,
  Button,
  Drawer,
  Spin,
  Row,
  Col,
  Divider,
  Badge,
} from 'antd';
import React, {
  useEffect,
  useState,
} from 'react';
import Notification from '@/assets/images/header/notification.react.svg?react';
import { getUserId } from '@/infrastructure/auth';
import { getAxios } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { logger } from '@/infrastructure/logger';
import { DATONG_ELEMENT_PARAMS, DATONG_PAGE_EID } from '@/infrastructure/reporter/datong/constant';
import { TopMenuButton } from '@/presentation/layouts/header/button';
import { NoticeList } from '@/presentation/layouts/header/notice/notice-list';
import { useStores } from '@/presentation/store';
import { toCamelCase } from '@raft/utils/src/case/camel-case';
import style from './index.module.scss';

export interface INoticeDetail {
  subTitle: string;
  sortNumber: number;
  content: string;
}

export interface INotice {
  readFlag: string;
  noticeId: string;
  title: string;
  noticeType: number;
  updateTime: string;
  noticeDetailList: INoticeDetail[];
}

export const Notice = () => {
  const { productId, pid, loaded } = useStores().ProductStore;
  const userId = getUserId();
  const [messageDrawerVisible, setMessageDrawerVisible] = useState(false);
  const [readCount, setReadCount] = useState(0);
  const [unReadCount, setUnReadCount] = useState(0);
  const [spining, setSpining] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [noticeList, setNoticeList] = useState<INotice[]>([]);
  const [sourceNoticeList, setSourceNoticeList] = useState([]);
  const badgeOffset: [number, number] = unReadCount >= 10 ? [0, 5] : [-5, 5];

  const getNotice = async () => {
    setSpining(true);
    try {
      const res = await getAxios().get(`/${API_PREFIX}/api/notice/pid/1/appId/1`);
      if (res) {
        const { notice = [], readCount = 0, unreadCount = 0 } = toCamelCase(res);
        setReadCount(Number.parseInt(readCount, 10));
        setUnReadCount(Number.parseInt(unreadCount, 10));
        setSourceNoticeList(notice);
      }
    } catch (e) {
      logger.error('getMessage:', e);
    }
    setSpining(false);
  };

  const setReadNotice = async (noticeIds: string[]) => {
    try {
      await getAxios().post(`/${API_PREFIX}/api/notice/pid/1/appId/1/update`, {
        appId: productId,
        pid,
        noticeIds,
      });
    } catch (e) {
      logger.error('setReadNotice:', e);
    }
  };

  const filterNoticeList = () => {
    let tempList: INotice[] = [];
    switch (activeTab) {
      case 'all':
        tempList = [...sourceNoticeList];
        break;
      case 'unRead':
        tempList = sourceNoticeList.filter((notice: INotice) => notice.readFlag === '0');
        break;
      case 'read':
        tempList = sourceNoticeList.filter((notice: INotice) => notice.readFlag === '1');
        break;
    }
    setNoticeList(tempList);
  };

  const handleReadAll = async () => {
    const unReadNoticeIds = sourceNoticeList
      .filter((notice: INotice) => notice.readFlag === '0')
      .map((notice: INotice) => notice.noticeId);
    await setReadNotice(unReadNoticeIds);
    await getNotice();
  };

  const handleReadNotice = async (noticeId: string) => {
    await setReadNotice([noticeId]);
    await getNotice();
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const unReactCountText = unReadCount > 0 ? `(${unReadCount})` : '';
  const unReadTab = `未读${unReactCountText}`;

  const reactCountText = readCount > 0 ? `(${readCount})` : '';
  const readTab = `已读${reactCountText}`;

  useEffect(() => {
    filterNoticeList();
  }, [activeTab, sourceNoticeList]);

  useEffect(() => {
    if (loaded) {
      getNotice();
    }
  }, [userId, loaded]);

  return <>
    <div
      className={style.messageCenter}
      dt-eid={DATONG_PAGE_EID.dashboardTopProblem}
      dt-params={`${DATONG_ELEMENT_PARAMS.homeTopBtnName}=消息`}
    >
      <Badge count={unReadCount} size="small" offset={badgeOffset}>
        <TopMenuButton onClick={() => setMessageDrawerVisible(true)}>
          <Notification />
          <span>消息中心</span>
        </TopMenuButton>
      </Badge>
    </div>
    <Drawer
      placement="right"
      visible={messageDrawerVisible}
      title={'消息'}
      onClose={() => setMessageDrawerVisible(false)}
    >
      <Spin spinning={spining}>
        <Row className={style.messageHeader}>
          <Col span={16}>
            <Space split={<Divider type="vertical" />}>
              <span
                onClick={() => handleTabClick('all')}
                className={activeTab === 'all' ? style.messageHeaderActive : ''}
              >
                全部
              </span>
              <span
                onClick={() => handleTabClick('unRead')}
                className={activeTab === 'unRead' ? style.messageHeaderActive : ''}
              >{unReadTab}</span>
              <span
                onClick={() => handleTabClick('read')}
                className={activeTab === 'read' ? style.messageHeaderActive : ''}
              >{readTab}</span>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button onClick={handleReadAll} type="text" icon={<ClearOutlined />}>一键已读</Button>
          </Col>
        </Row>
        <NoticeList
          data={noticeList}
          onReadNotice={handleReadNotice}
        />
      </Spin>
    </Drawer>
  </>;
};
